import { useEffect, useState } from "react";
import styles from "./lead.module.scss";
import { ReactComponent as HouseIcon } from "assets/house.svg";
import { ReactComponent as CloseIcon } from "assets/close.svg";
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import { ReactComponent as CrosshairIcon } from "assets/crosshair.svg";
import { ReactComponent as ExpandIcon } from "assets/expand.svg";
import { store } from "stores/Store";

import PractitionerInfo from "./PractitionerInfo";
import LeadForm from "./LeadForm";
import LocationSearch from "./LocationSearch";
import useExternalApiService from "helpers/ExternalApiService";
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import { useNavigate } from "react-router-dom";
import { useDebouncedState } from "helpers/useDebouncedState";

interface Props {
  initialSearchValue: string;
  extraArgs?: string;
  practitionerInfo: any;
  isDesktopMap?: boolean;
  onFormStateChange: (s: boolean) => void;
  onFormViewChange: (v: boolean) => void;
  onClose: (v?: boolean) => void;
  onViewDuplicate?: () => void;
}

export interface Practs {
  name: string;
  firstname: string;
  lastname: string;
  companyname: string;
  address: string;
  state: string;
  city: string;
  zipcode: string;
  specialty: string;
  phone: string;
  grade: number;
  email: string;
  id?: string;
  ready_to_purchase?: boolean;
  latitude?: number;
  longitude?: number;
  country?: string;
};

interface PlaceSuggestion {
  description: string;
  place_id: string;
  structured_formatting: {
    main_text: string;
    secondary_text: string;
  };
  types: string[];
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
interface PlacesResponse {
  success: boolean;
  error_msg: string;
  places: PlaceSuggestion[];
}

const LeadSteps: React.FC<Props> = ({ initialSearchValue, extraArgs, practitionerInfo, isDesktopMap = false, onClose, onFormStateChange, onFormViewChange, onViewDuplicate }) => {
  const [ debouncedSearchStr, searchStr, setSearchStr ] = useDebouncedState(initialSearchValue);
  const [ selectedPractitioner, setSelectedPractitioner ] = useState<Partial<Practs> | null>(null);
  const [ viewForm, setViewForm ] = useState(practitionerInfo ? "form" : "");
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [ currentLocation, setCurrentLocation ] = useState<any>(null);
  const [ selectedLocation, setSelectedLocation ] = useState<any>(null);
  const [suggestions, setSuggestions] = useState<any[]>([]);
  const [isPresetDropdownOpen, setIsPresetDropdownOpen] = useState(false);

  const apiUrl = process.env.REACT_APP_EAI_URL;
  const { postData } = useExternalApiService(apiUrl);
  
  const presetOptions = [
    { value: "acupuncture", label: "Acupuncture" },
    { value: "allergies", label: "Allergies" },
    { value: "allergy", label: "Allergy" },
    { value: "alternative medicine", label: "Alternative Medicine" },
    { value: "amin", label: "Amin" },
    { value: "arthritis", label: "Arthritis" },
    { value: "cardiologist", label: "Cardiologist" },
    { value: "cardiology", label: "Cardiology" },
    { value: "cardiovascular", label: "Cardiovascular" },
    { value: "chiropractic", label: "Chiropractic" },
    { value: "chiropractor", label: "Chiropractor" },
    { value: "chiropractors", label: "Chiropractors" },
    { value: "clinical", label: "Clinical" },
    { value: "cnc", label: "CNC" },
    { value: "coach", label: "Coach" },
    { value: "compounding pharmacy", label: "Compounding Pharmacy" },
    { value: "cranial sacral", label: "Cranial Sacral" },
    { value: "d.o.", label: "D.O." },
    { value: "dc", label: "DC" },
    { value: "dds", label: "DDS" },
    { value: "dentist", label: "Dentist" },
    { value: "dermatologist", label: "Dermatologist" },
    { value: "dietician", label: "Dietician" },
    { value: "dnm", label: "DNM" },
    { value: "dnp", label: "DNP" },
    { value: "do", label: "DO" },
    { value: "doctor", label: "Doctor" },
    { value: "doctors", label: "Doctors" },
    { value: "dpt", label: "DPT" },
    { value: "emg", label: "EMG" },
    { value: "family medicine", label: "Family Medicine" },
    { value: "fnp", label: "FNP" },
    { value: "functional medicine", label: "Functional Medicine" },
    { value: "gynecologist", label: "Gynecologist" },
    { value: "gynecology", label: "Gynecology" },
    { value: "healing", label: "Healing" },
    { value: "health", label: "Health" },
    { value: "health coach", label: "Health Coach" },
    { value: "health store", label: "Health Store" },
    { value: "holistic", label: "Holistic" },
    { value: "homeopathic", label: "Homeopathic" },
    { value: "homeopathy", label: "Homeopathy" },
    { value: "hrt", label: "HRT" },
    { value: "integrated", label: "Integrated" },
    { value: "integrative", label: "Integrative" },
    { value: "internal medicine", label: "Internal Medicine" },
    { value: "l.ac", label: "L.Ac" },
    { value: "lac", label: "Lac" },
    { value: "m.d.", label: "M.D." },
    { value: "md", label: "MD" },
    { value: "mdvip", label: "MDVIP" },
    { value: "med spa", label: "Med Spa" },
    { value: "medical spa", label: "Medical Spa" },
    { value: "mph", label: "MPH" },
    { value: "natural medicine", label: "Natural Medicine" },
    { value: "naturopath", label: "Naturopath" },
    { value: "naturopathic", label: "Naturopathic" },
    { value: "nd", label: "ND" },
    { value: "neuro", label: "Neuro" },
    { value: "nmd", label: "NMD" },
    { value: "nourishing", label: "Nourishing" },
    { value: "nutrition", label: "Nutrition" },
    { value: "nutritionist", label: "Nutritionist" },
    { value: "ob/gyn", label: "OB/GYN" },
    { value: "orthopedic", label: "Orthopedic" },
    { value: "pa", label: "PA" },
    { value: "pa-c", label: "PA-C" },
    { value: "pain management", label: "Pain Management" },
    { value: "pediatrician", label: "Pediatrician" },
    { value: "pediatrics", label: "Pediatrics" },
    { value: "pharmacist", label: "Pharmacist" },
    { value: "pharmacy", label: "Pharmacy" },
    { value: "phd", label: "PhD" },
    { value: "physicians", label: "Physicians" },
    { value: "plastic surgery", label: "Plastic Surgery" },
    { value: "post", label: "Post" },
    { value: "preventative medicine", label: "Preventative Medicine" },
    { value: "pt", label: "PT" },
    { value: "rd", label: "RD" },
    { value: "rdn", label: "RDN" },
    { value: "registered dietitian", label: "Registered Dietitian" },
    { value: "rn", label: "RN" },
    { value: "supplements", label: "Supplements" },
    { value: "surgeon", label: "Surgeon" },
    { value: "urgent care", label: "Urgent Care" },
    { value: "weight loss", label: "Weight Loss" },
    { value: "weightloss", label: "Weightloss" },
    { value: "wellness", label: "Wellness" },
  ];

  const handlePresetSelect = (value: string) => {
    setSearchStr(value);
    setIsPresetDropdownOpen(false);
  };

  useEffect(() => {
    if ("geolocation" in navigator) {
      navigator.geolocation.getCurrentPosition((position) => {
        const lat = position.coords.latitude;
        const lng = position.coords.longitude;
        setCurrentLocation({
          description: "Current Location",
          place_id: "current_location",
          terms: [
            { value: "Current Location" },
            { value: `${lat.toFixed(6)}, ${lng.toFixed(6)}` }
          ],
          geometry: {
            location: {
              lat,
              lng
            }
          }
        });
      });
    }

    if (extraArgs === "edit") {
      setViewForm("form");
      const a = store.currentPractitioner.addresses[0];

      setSelectedPractitioner({
        name: store.currentPractitioner?.name,
        address: a?.address,
        phone: a?.phone,
        email: store.currentPractitioner?.email,
        firstname: store.currentPractitioner?.firstname,
        lastname: store.currentPractitioner?.lastname,
        companyname: store.currentPractitioner?.companyname,
        city: a?.city,
        zipcode: a?.zipcode,
        state: a?.state,
        specialty: store.currentPractitioner?.specialty,      
        id: store.currentPractitioner?.id,
        ready_to_purchase: Boolean(store.currentPractitioner?.ready_to_purchase),
        latitude: a?.latitude,
        longitude: a?.longitude,
        country: a?.country,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onSuggestionClick = async (suggestion: any) => {
    const result = await postData('/api/google_places_details', {
      place_id: suggestion.place_id,
    });
    //console.log(result);
    setSelectedPractitioner(result);

    //console.log(viewForm, selectedPractitioner);

  }


  const getPlacesNearbySearch = async (input: string, locationOverride?: any) => {
    // Use locationOverride if provided, otherwise use selectedLocation
    const locationToUse = locationOverride || selectedLocation;

    if (locationToUse && locationToUse.lat && locationToUse.lng) {
      const locationString = `${locationToUse.lat},${locationToUse.lng}`;

      // Dynamic radius based on location type
      // Current location (tight, precise): 1km for dense areas
      // Custom location (broader): 10km for city-wide searches
      const radius = locationToUse.place_id === 'current_location' ? 1000 : 10000;

      try {
        // Use NEW Places API Text Search for practitioner searches
        // Combines query with location name for better city-specific results
        const result = await postData('/api/google_places_nearbysearch', {
          query: input,
          location: locationString,
          location_name: locationToUse.description || '',  // "Los Gatos, CA, USA"
          radius: radius
        });
        return result;
      } catch (error) {
        throw error;
      }
    } else {
      // Fallback to current location if no selected location
      return new Promise((resolve, reject) => {
        if ("geolocation" in navigator) {
          navigator.geolocation.getCurrentPosition(async (position) => {
            const lat = position.coords.latitude;
            const lng = position.coords.longitude;
            const currentLocationString = `${lat},${lng}`;

            try {
              const result = await postData('/api/google_places_nearbysearch', {
                query: input,
                location: currentLocationString,
                radius: 1000,  // 1km for current location
                rankby: 'distance'
              });
              resolve(result);
            } catch (error) {
              reject(error);
            }
          }, (error) => {
            reject(error);
          });
        } else {
          reject(new Error('Geolocation not supported'));
        }
      });
    }
  };

  useEffect(() => {
    if (debouncedSearchStr) {
      getPlacesNearbySearch(debouncedSearchStr).then((response) => {
        const r = response as any; // Nearby Search returns 'results' not 'places'
        console.log('Nearby Search Response:', r); // Debug log

        // Check if we have valid results array
        if (!r || !r.success) {
          console.log('API call failed or returned unsuccessful');
          setSuggestions([]);
          return;
        }

        const results = r.results || [];
        if (!Array.isArray(results) || results.length === 0) {
          console.log('No results or invalid results array');
          setSuggestions([]);
          return;
        }

        // Convert Nearby Search results to format expected by suggestions
        const formattedResults = results.map((place: any) => ({
          description: place.name + (place.formatted_address ? `, ${place.formatted_address}` : ''),
          place_id: place.place_id,
          structured_formatting: {
            main_text: place.name,
            secondary_text: place.vicinity || place.formatted_address || '',
          },
          types: place.types || [],
          // Include rating and review data for display
          rating: place.rating,
          user_ratings_total: place.user_ratings_total,
          prominence_score: place.prominence_score,
          quality_score: place.quality_score,
          combined_score: place.combined_score,
          // Business info (all FREE from Nearby Search API)
          business_status: place.business_status,
          opening_hours: place.opening_hours,
          price_level: place.price_level
        }));
        setSuggestions(formattedResults);
      }).catch((err) => {
        console.error('Nearby Search error:', err);
        setSuggestions([]);
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearchStr, selectedLocation]);
  
  useEffect(() => {
    if (viewForm === "form" || viewForm === "empty-form") {
      onFormViewChange(true);
    } else  {
      onFormViewChange(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewForm]);

  useEffect(() => {
    if (practitionerInfo) {
      setViewForm("form");
    }
  }, [practitionerInfo]);

  if ((viewForm === "form" || viewForm === "empty-form") && (practitionerInfo || selectedPractitioner)) {
    return(
      <LeadForm
        practitionerInfo={practitionerInfo || selectedPractitioner || null}
        onFormStateChange={(s) => onFormStateChange(s)}
        onFormSubmitted={() => onClose(true)}
        onViewDuplicate={onViewDuplicate}
        onNavigate={!practitionerInfo ? (v) => {
          if (v === "lead") {
            setSelectedPractitioner(null);
            setViewForm("");
          }
          if (v === "draft") {
            alert("save draft")
            setSelectedPractitioner(null);
          }
          if (v === "submit") {
            alert("save lead")
            setSelectedPractitioner(null);
          }
          if (v === "backedit") {
            // close and open practitoner modal
            // navigate(`/?${new URLSearchParams({prompt: 'practitioners', toggle: "false", targetSubTab: "lead"}).toString()}`)
          }
        } : undefined} />
    );
  };

  if (!viewForm && selectedPractitioner) {
    return (
      <PractitionerInfo 
        practitioner={selectedPractitioner}
        onNavigate={(v) => {
          if (v === "lead") {
            setSelectedPractitioner(null);
          }
          if (v === "form") {
            setViewForm(v);
          }
          if (v === "empty-form") {
            setViewForm(v)
          }
        }} />
    );
  };

  return (
    <div className={styles["create-lead-wrapper"]}>
      <div className={styles["search-bar"]} style={{ marginTop: isDesktopMap ? 10 : 0 }}>
        <HouseIcon />
        <input
          autoFocus
          value={searchStr}
          onChange={(e) => setSearchStr(e.target.value)}
          type="text"
          className={styles["search-input"]}
        />
        <CloseIcon width={27} onClick={() => setSearchStr("")} />
        <div style={{
          fontSize: "12px",
          color: "#4F7D9C",
          fontWeight: "bold",
        }}
          onClick={() => onClose(false)}>
          CANCEL
        </div>
      </div>
      <div className={styles["sub-search-bar-no-margin"]}>PICK THESE MATCHES FROM GOOGLE MAPS</div>
      
      <LocationSearch
        selectedLocation={selectedLocation}
        onLocationSelect={(location) => {
          setSelectedLocation(location);
          // Only trigger search if user manually selected location
          // Use simpler search term for better location-based results
          const searchTerm = searchStr || "doctor";
          // Pass location directly to avoid state timing issues
          getPlacesNearbySearch(searchTerm, location).then((response) => {
            const r = response as any;
            if (r.success && r.results && Array.isArray(r.results)) {
              // Convert Nearby Search results to format expected by suggestions
              const formattedResults = r.results.map((place: any) => ({
                description: place.name + (place.formatted_address ? `, ${place.formatted_address}` : ''),
                place_id: place.place_id,
                structured_formatting: {
                  main_text: place.name,
                  secondary_text: place.vicinity || place.formatted_address || '',
                },
                types: place.types || [],
                rating: place.rating,
                user_ratings_total: place.user_ratings_total,
                prominence_score: place.prominence_score,
                quality_score: place.quality_score,
                combined_score: place.combined_score,
                business_status: place.business_status,
                opening_hours: place.opening_hours,
                price_level: place.price_level
              }));
              setSuggestions(formattedResults);
            } else {
              setSuggestions([]);
            }
          }).catch((err) => {
            console.error('Nearby Search error:', err);
            setSuggestions([]);
          });
        }}
      />
            
      <div className={styles["preset-section"]}>
        <div className={styles["preset-header"]} onClick={() => setIsPresetDropdownOpen(!isPresetDropdownOpen)}>
          <div className={styles["preset-header-text"]}>Quick Search Ideas</div>
          <ExpandIcon 
            className={`${styles["preset-expand-icon"]} ${isPresetDropdownOpen ? styles["expanded"] : ""}`}
            style={{width: '15px', height: '15px'}}
          />
        </div>
        {isPresetDropdownOpen && (
          <div className={styles["preset-options"]}>
            {presetOptions.map((option) => (
              <div
                key={option.value}
                className={styles["preset-option"]}
                onClick={() => handlePresetSelect(option.value)}
              >
                {option.label}
              </div>
            ))}
          </div>
        )}
      </div>
      
      <ul className={styles["practitioner-list"]}>
          {suggestions.length > 0 ? (
            suggestions.map((suggestion, index) => (
              <div
                key={index}
                className={styles.suggestionItem}
                onClick={() => onSuggestionClick(suggestion)}
              >
                <div className={styles.suggestionHeader}>
                  {suggestion.structured_formatting?.main_text || suggestion.description}
                  {suggestion.rating && suggestion.user_ratings_total > 0 ? (
                    <span style={{marginLeft: '8px', fontSize: '14px', color: '#f5a623'}}>
                      ⭐ {suggestion.rating.toFixed(1)}
                      <span style={{color: '#888', fontSize: '12px'}}> ({suggestion.user_ratings_total})</span>
                    </span>
                  ): null}
                </div>
                <div className={styles.suggestionSubtext}>
                  {suggestion.structured_formatting?.secondary_text || ''}
                  {suggestion.business_status === 'OPERATIONAL' && suggestion.opening_hours?.open_now !== undefined && (
                    <span style={{marginLeft: '8px', fontSize: '11px', color: suggestion.opening_hours.open_now ? '#00a86b' : '#d32f2f'}}>
                      • {suggestion.opening_hours.open_now ? 'Open now' : 'Closed'}
                    </span>
                  )}
                </div>
              </div>
            ))
          ) : (
            <div 
              //onClick={() => currentLocation && onSuggestionClick(currentLocation)} 
              className={styles.noSuggestions}
            >
              {/*<CrosshairIcon  style={{width: '25px', height: '25px', paddingLeft: '10px'}}/> Use current location */}
            </div>
          )}
      </ul>
      <div className={styles["button-group"]}>
        <p>Can't Find Practitioner?</p>
        <button
          onClick={() => {
            setViewForm("empty-form");
            setSelectedPractitioner({});
          }}
          className={styles["manual-button"]}
          type="button">
            ENTER MANUALLY
        </button>
      </div>
    </div>
  );
};

export default LeadSteps;